import { useState, useEffect } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { useLocale } from "../lib/useLocale";
import { LANGS } from "../i18n";
import AmbientSound from "./AmbientSound";
import ChatWidget from "./ChatWidget";
import { Menu, X, Home, Compass, Briefcase, LogOut, LayoutDashboard, Globe, ChevronDown, BookOpen, Newspaper } from "lucide-react";

const navItems = [
  { to: "/", key: "home", icon: Home, end: true },
  { to: "/retreats", key: "retreats", icon: Compass },
  { to: "/story", key: "story", icon: BookOpen },
  { to: "/blog", key: "blog", icon: Newspaper },
  { to: "/trips", key: "journeys", icon: Briefcase },
];

export default function Layout() {
  const { user, login, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const { lang, path: lp } = useLocale();
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => { i18n.changeLanguage(lang); document.documentElement.lang = lang; }, [lang, i18n]);
  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);

  const basePath = (() => {
    const parts = location.pathname.split("/");
    if (LANGS.includes(parts[1]) && parts[1] !== "en") parts.splice(1, 1);
    const p = parts.join("/");
    return p === "" ? "/" : p;
  })();

  const switchLang = (l) => {
    localStorage.setItem("rr_lang_seen", "1");
    navigate(l === "en" ? (basePath || "/") : `/${l}${basePath === "/" ? "" : basePath}`);
    setLangOpen(false); setOpen(false);
  };

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const altHref = (l) => (l === "en" ? origin + (basePath || "/") : `${origin}/${l}${basePath === "/" ? "" : basePath}`);

  const handleLogout = async () => { await logout(); navigate(lp("/")); };

  return (
    <div className="min-h-screen bg-rr-bg text-rr-ivory flex flex-col">
      <Helmet>
        <html lang={lang} />
        <meta name="description" content="Rokert Retreats — a wellness & longevity house rooted in Sri Lankan Ayurveda. Private coastal retreats for body and mind." />
        {LANGS.map((l) => <link key={l} rel="alternate" hrefLang={l} href={altHref(l)} />)}
        <link rel="alternate" hrefLang="x-default" href={altHref("en")} />
      </Helmet>

      <header className="glass-nav fixed top-0 inset-x-0 z-50" data-testid="top-nav">
        <div className="max-w-7xl mx-auto px-5 md:px-10 h-[74px] flex items-center justify-between">
          <Link to={lp("/")} data-testid="brand-logo" className="flex items-center gap-3 group">
            <img src="/media/rokert-emblem.png" alt="Rokert Retreats emblem" className="h-10 w-auto" />
            <span className="font-wordmark lowercase text-[19px] font-light tracking-[0.16em] text-rr-ivory hidden sm:inline transition-colors">rokert<span className="text-rr-gold"> retreats</span></span>
          </Link>

          <nav className="hidden md:flex items-center gap-11">
            {navItems.map((n) => (
              <NavLink key={n.to} to={lp(n.to)} end={n.end} data-testid={`nav-${n.key}`}
                className={({ isActive }) => `font-body text-[12px] tracking-[0.12em] uppercase link-underline ${isActive ? "text-rr-gold" : "text-rr-ivory/70 hover:text-rr-ivory"}`}>
                {t(`nav.${n.key}`)}
              </NavLink>
            ))}
            {user?.is_admin && <NavLink to="/admin" data-testid="nav-admin" className="font-body text-[12px] tracking-[0.12em] uppercase text-rr-gold link-underline">{t("nav.admin")}</NavLink>}
          </nav>

          <div className="hidden md:flex items-center gap-5">
            <div className="relative">
              <button onClick={() => setLangOpen((v) => !v)} data-testid="lang-switcher" className="flex items-center gap-1.5 text-rr-ivory/70 hover:text-rr-gold text-[12px] uppercase tracking-wider">
                <Globe className="w-4 h-4" strokeWidth={1.5} /> {lang} <ChevronDown className="w-3 h-3" />
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-3 w-40 rounded-xl bg-rr-surface border border-rr-line py-2 shadow-2xl" data-testid="lang-menu">
                  {LANGS.map((l) => (
                    <button key={l} onClick={() => switchLang(l)} data-testid={`lang-${l}`}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-rr-gold/10 ${l === lang ? "text-rr-gold" : "text-rr-ivory/80"}`}>{t(`lang.${l}`)}</button>
                  ))}
                </div>
              )}
            </div>
            {user ? (
              <>
                {user.picture
                  ? <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover border border-rr-gold/40" />
                  : <div className="w-9 h-9 rounded-full bg-rr-gold/15 flex items-center justify-center text-rr-gold text-sm">{user.name?.[0] || "G"}</div>}
                <button onClick={handleLogout} data-testid="logout-btn" className="text-rr-muted hover:text-rr-ivory transition-colors"><LogOut className="w-5 h-5" strokeWidth={1.4} /></button>
              </>
            ) : (
              <button onClick={() => login("/trips")} data-testid="nav-signin-btn" className="btn-outline px-7 py-2.5 text-[11px] uppercase">{t("nav.signin")}</button>
            )}
          </div>

          <button className="md:hidden text-rr-ivory" onClick={() => setOpen(true)} data-testid="mobile-menu-open"><Menu className="w-6 h-6" strokeWidth={1.4} /></button>
        </div>
      </header>

      {open && (
        <div className="fixed inset-0 z-[60] md:hidden" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-72 bg-rr-surface border-l border-rr-line p-7 flex flex-col overflow-y-auto">
            <button className="self-end text-rr-ivory mb-8" onClick={() => setOpen(false)} data-testid="mobile-menu-close"><X className="w-6 h-6" strokeWidth={1.4} /></button>
            {navItems.map((n) => (
              <NavLink key={n.to} to={lp(n.to)} end={n.end} onClick={() => setOpen(false)} className="font-heading text-3xl py-3 text-rr-ivory">{t(`nav.${n.key}`)}</NavLink>
            ))}
            {user?.is_admin && <NavLink to="/admin" onClick={() => setOpen(false)} className="font-heading text-3xl py-3 text-rr-gold">{t("nav.admin")}</NavLink>}
            <NavLink to={lp("/policy")} onClick={() => setOpen(false)} data-testid="mobile-policy-link" className="font-body text-sm py-3 text-rr-ivory/60 tracking-wide">{t("nav.policy")}</NavLink>
            <div className="flex flex-wrap gap-2 mt-4">
              {LANGS.map((l) => (
                <button key={l} onClick={() => switchLang(l)} data-testid={`mobile-lang-${l}`}
                  className={`px-3 py-1.5 rounded-full text-xs uppercase border ${l === lang ? "border-rr-gold text-rr-gold" : "border-rr-line text-rr-ivory/70"}`}>{l}</button>
              ))}
            </div>
            <div className="mt-8">
              {user ? <button onClick={handleLogout} className="btn-outline w-full py-3 uppercase text-xs">{t("nav.signout")}</button>
                    : <button onClick={() => login("/trips")} className="btn-gold w-full py-3 uppercase text-xs">{t("nav.signin")}</button>}
            </div>
            <p className="text-rr-ivory/35 text-[11px] mt-8">{t("foot.regName")} · {t("foot.regNo")}</p>
          </div>
        </div>
      )}

      <main className="flex-1 pt-[74px] pb-20 md:pb-0"><Outlet /></main>

      <footer className="bg-rr-obsidian border-t border-rr-line mt-24 hidden md:block">
        <div className="max-w-7xl mx-auto px-10 py-16 grid grid-cols-3 gap-10">
          <div>
            <div className="flex items-center gap-3">
              <img src="/media/rokert-emblem.png" alt="Rokert Retreats emblem" className="h-14 w-auto" />
              <span className="font-wordmark lowercase text-2xl font-light tracking-[0.14em] text-rr-ivory">rokert<span className="text-rr-gold"> retreats</span></span>
            </div>
            <p className="text-rr-ivory/55 text-sm max-w-xs leading-relaxed mt-5">{t("foot.tagline")}</p>
            <p className="text-rr-ivory/40 text-xs mt-6" data-testid="footer-reg-name">{t("foot.regName")}</p>
            <p className="text-rr-ivory/40 text-xs">{t("foot.regNo")}</p>
          </div>
          <div className="font-body text-sm text-rr-ivory/55 space-y-3">
            <p className="overline mb-5">{t("foot.journeysTitle")}</p>
            {t("foot.j", { returnObjects: true }).map((j) => <p key={j}>{j}</p>)}
          </div>
          <div className="font-body text-sm text-rr-ivory/55 space-y-3">
            <p className="overline mb-5">{t("foot.conciergeTitle")}</p>
            <Link to={lp("/story")} data-testid="footer-story-link" className="inline-block text-rr-ivory/70 hover:text-rr-gold link-underline pt-1">{t("foot.story")}</Link>
            <br />
            <Link to={lp("/blog")} data-testid="footer-blog-link" className="inline-block text-rr-ivory/70 hover:text-rr-gold link-underline">{t("nav.blog")}</Link>
            <br />
            <Link to={lp("/policy")} data-testid="footer-policy-link" className="inline-block text-rr-ivory/70 hover:text-rr-gold link-underline">{t("foot.policy")}</Link>
          </div>
        </div>
        <div className="border-t border-rr-line py-6 text-center text-xs text-rr-ivory/40 tracking-wide">© {new Date().getFullYear()} Rokert Lanka (Private) Limited · Reg. No. PV00269087</div>
      </footer>

      <nav className="glass-bottom fixed bottom-0 inset-x-0 z-50 md:hidden" data-testid="bottom-nav">
        <div className="flex items-center justify-around h-16">
          {navItems.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink key={n.to} to={lp(n.to)} end={n.end} className={({ isActive }) => `flex flex-col items-center gap-1 ${isActive ? "text-rr-gold" : "text-rr-muted"}`}>
                <Icon className="w-5 h-5" strokeWidth={1.4} /><span className="text-[10px] tracking-wide">{t(`nav.${n.key}`)}</span>
              </NavLink>
            );
          })}
          {user?.is_admin && (
            <NavLink to="/admin" className={({ isActive }) => `flex flex-col items-center gap-1 ${isActive ? "text-rr-gold" : "text-rr-muted"}`}>
              <LayoutDashboard className="w-5 h-5" strokeWidth={1.4} /><span className="text-[10px] tracking-wide">{t("nav.admin")}</span>
            </NavLink>
          )}
        </div>
      </nav>

      <AmbientSound />
      <ChatWidget />
    </div>
  );
}
